import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import FavoritesStats from './FavoritesStats';
import FavoritesTrendGraph from './FavoritesTrendGraph';
import FavoritersList from './FavoritersList';
import ComposeMessage from './ComposeMessage';
import VendorSentMessages from './VendorSentMessages';
import VendorReceivedMessages from './VendorReceivedMessages';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const VendorFavoritesDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');
  const [timeRange, setTimeRange] = useState(30);

  useEffect(() => {
    if (user && ['producer', 'hub'].includes(user.role)) {
      fetchDashboardData();
      fetchSegments();
    }
  }, [user, timeRange]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, trendsRes] = await Promise.all([
        fetch('/api/favorites/dashboard', { headers: getHeaders() }),
        fetch(`/api/favorites/dashboard/trends?days=${timeRange}`, { headers: getHeaders() })
      ]);
      
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
      if (trendsRes.ok) {
        const trendsData = await trendsRes.json();
        setTrends(trendsData || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSegments = async () => {
    try {
      const res = await fetch('/api/favorites/dashboard/segments', { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSegments(data || []);
      }
    } catch (error) {
      console.log('Messaging not available for this role');
    }
  };

  const tabs = [
    { id: 'stats', label: '📊 Stats' },
    { id: 'trends', label: '📈 Trends' },
    { id: 'followers', label: '❤️ Followers' },
    { id: 'sent', label: '📤 Sent' },
    { id: 'received', label: '📬 Received' },
  ];

  if (segments.length > 0) {
    tabs.push({ id: 'message', label: '✉️ Message' });
  }

  if (!user || !['producer', 'hub'].includes(user.role)) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">This dashboard is only available for producers and hubs</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-24 bg-gray-200 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="vendor-favorites-dashboard">
      <div className="flex justify-end mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Time Range:</span>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(parseInt(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value={7}>7 Days</option>
            <option value={30}>30 Days</option>
            <option value={90}>90 Days</option>
          </select>
        </div>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                pb-3 px-1 text-sm font-medium border-b-2 transition
                ${activeTab === tab.id 
                  ? 'border-green-500 text-green-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="tab-content">
        {activeTab === 'stats' && stats && (
          <FavoritesStats stats={stats} />
        )}

        {activeTab === 'trends' && (
          <div>
            <FavoritesTrendGraph data={trends} timeRange={timeRange} />
          </div>
        )}

        {activeTab === 'followers' && (
          <FavoritersList vendorId={user._id} />
        )}

        {activeTab === 'message' && segments.length > 0 && (
          <ComposeMessage segments={segments} onSend={fetchDashboardData} />
        )}

        {activeTab === 'sent' && (
          <VendorSentMessages />
        )}

        {activeTab === 'received' && (
          <VendorReceivedMessages />
        )}
      </div>
    </div>
  );
};

export default VendorFavoritesDashboard;
